import React, { useCallback, useMemo, useState } from 'react';
import { QueryBuilderAntD, AntDValueEditor, AntDValueSelector } from '@react-querybuilder/antd';
import { formatQuery, QueryBuilder, defaultOperators } from 'react-querybuilder';
import equal from 'fast-deep-equal/react';

import 'react-querybuilder/dist/query-builder.css';

import { deviceTypes, referrerTypes, browsers, deviceBrands, operatingSystems } from 'shared/constants/matomo';
import { QUESTION_TYPES } from 'shared/constants/survey';
import config from 'shared/constants/results';

import CountrySelect from 'components/CountrySelect';
import useFormatMessage from 'hooks/useFormatMessage';
import LanguageSelect from 'components/LanguageSelect';
import { Select } from 'antd';
import SurveyGroupSelect from 'components/SurveyGroupSelect';
import messages from './messages';
import SurveyOptionSelector from './SurveyQuestionOptionSelector';
import SurveyQuestionSelector from './SurveyQuestionSelector';
import SegmentSelector from './SegmentSelector';

import './query-builder.less';

export const emptyQuery = { combinator: 'and', rules: [{}] };

const multiSelectOperators = defaultOperators.filter((op) => ['in', 'notIn'].includes(op.name));
const textOperators = defaultOperators.filter((op) => ['=', '!=', 'contains', 'doesNotContain', 'beginsWith', 'endsWith', 'doesNotBeginWith', 'doesNotEndWith', 'null', 'notNull'].includes(op.name));
const numberOperators = defaultOperators.filter((op) => ['=', '!=', '<', '<=', '>', '>=', 'between', 'notBetween', 'null', 'notNull'].includes(op.name));
const dateOperators = defaultOperators.filter((op) => ['between', 'notBetween'].includes(op.name));
const booleanOperators = defaultOperators.filter((op) => ['=', '!=', 'null', 'notNull'].includes(op.name));
const equalOperators = defaultOperators.filter((op) => op.name === '=');

const questionTypeValues = Object.keys(QUESTION_TYPES).map((name) => ({ name, label: name }));
const deviceTypeValues = Object.keys(deviceTypes).map((name) => ({ name, label: name }));
const browserValues = Object.keys(browsers).map((name) => ({ name, label: browsers[name] }));
const referrerTypeValues = Object.keys(referrerTypes).map((name) => ({ name, label: name }));
const deviceBrandValues = Object.keys(deviceBrands).map((name) => ({ name, label: deviceBrands[name] }));
const operatingSystemValues = Object.keys(operatingSystems).map((name) => ({ name, label: operatingSystems[name] }));

const SearchableValueSelector = (props) => (
  <AntDValueSelector
    showSearch
    style={{ minWidth: 200 }}
    {...props}
  />
);

const CustomValueEditor = (props) => {
  const { type, customerId } = props.fieldData;

  let component;

  switch (type) {
    case 'country':
      component = <CountrySelect value={props.value} onChange={props.handleOnChange} mode="multiple" style={{ minWidth: 200 }} />;
      break;
    case 'language':
      component = <LanguageSelect value={props.value} onChange={props.handleOnChange} mode="multiple" style={{ minWidth: 200 }} />;
      break;
    case 'boolean':
      component = <Select value={props.value} onChange={props.handleOnChange} options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} />;
      break;
    case 'surveyGroup':
      component = <SurveyGroupSelect value={props.value} onChange={props.handleOnChange} customerId={customerId} style={{ minWidth: 200 }} multiple />;
      break;
    case 'surveyQuestion':
      component = <SurveyQuestionSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    case 'surveyOption':
      component = <SurveyOptionSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    case 'segment':
      component = <SegmentSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    default:
  }

  if (component) {
    return (
      <div>
        {component}
      </div>
    );
  }

  return (
    <AntDValueEditor
      showSearch
      style={{ minWidth: 200 }}
      {...props}
    />
  );
};

const getDefaultValue = (rule, { fieldData }) => {
  switch (fieldData.type) {
    case 'country':
    case 'language':
    case 'questionType':
    case 'deviceType':
    case 'deviceBrand':
    case 'operatingSystem':
    case 'browser':
    case 'referrerType':
    case 'surveyGroup':
    case 'surveyQuestion':
    case 'surveyOption':
      return [];
    case 'boolean':
      return true;
    case 'date':
      return [new Date(), new Date()];
    default:
      return '';
  }
};

const getFieldProps = (field) => {
  let operators = textOperators;
  let valueEditorType = 'text';
  let inputType;
  let dataType;
  let values;

  switch (field.type) {
    case 'country':
      operators = multiSelectOperators;
      break;
    case 'language':
      operators = multiSelectOperators;
      break;
    case 'number':
      inputType = 'number';
      operators = numberOperators;
      break;
    case 'boolean':
      operators = booleanOperators;
      break;
    case 'date':
      inputType = 'date';
      dataType = 'date';
      operators = dateOperators;
      break;
    case 'questionType':
      values = questionTypeValues;
      break;
    case 'deviceType':
      values = deviceTypeValues;
      break;
    case 'deviceBrand':
      values = deviceBrandValues;
      break;
    case 'operatingSystem':
      values = operatingSystemValues;
      break;
    case 'browser':
      values = browserValues;
      break;
    case 'referrerType':
      values = referrerTypeValues;
      break;
    case 'surveyGroup':
    case 'surveyQuestion':
    case 'surveyOption':
      operators = multiSelectOperators;
      break;

    default:
  }

  if (values) {
    operators = multiSelectOperators;
    valueEditorType = 'multiselect';
  }

  return { operators, valueEditorType, inputType, dataType, values };
};

const getFields = ({ $t, customerId }) => Object.keys(config.tables).map(table => ({
  label: $t(messages[table]),
  options: Object.entries(config.fields).filter(([, field]) => field.table === table && field.segment !== false).map(([name, field]) => ({
    ...getFieldProps(field),
    ...field,
    customerId,
    name,
    label: messages[name] ? $t(messages[name]) : name,
  })),
})).filter(f => f.options?.length > 0);

const QueryBuilderInput = ({ value, onChange, fields, format = 'json_without_ids', customerId }) => {
  const [query, setQuery] = useState(value || emptyQuery);
  const $t = useFormatMessage();

  const onQueryChange = useCallback((updatedQuery) => {
    setQuery(updatedQuery);
    const queryWithoutIds = JSON.parse(formatQuery(updatedQuery, format));
    if (!equal(queryWithoutIds, query)) {
      onChange(queryWithoutIds);
    }
  }, [format, onChange, query]);

  const queryFields = useMemo(() => {
    if (fields) return fields;

    return [
      {
        label: 'Segments',
        options: [{
          label: 'Segment',
          name: 'segment',
          operators: equalOperators,
          type: 'segment',
        }],
      },
      ...getFields({ $t, customerId }),
    ];
  }, [fields, $t, customerId]);

  return (
    <QueryBuilderAntD>
      <QueryBuilder
        fields={queryFields}
        query={query}
        onQueryChange={onQueryChange}
        showNotToggle
        listsAsArrays
        addRuleToNewGroups
        controlElements={{
          valueEditor: CustomValueEditor,
          fieldSelector: SearchableValueSelector,
        }}
        getDefaultValue={getDefaultValue}
      // parseNumbers="strict-limited"
      />
    </QueryBuilderAntD>
  );
};

export default QueryBuilderInput;
